import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { GridColumnVisibilityModel, GridRowParams } from '@mui/x-data-grid-pro'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { EmptyCustomers } from 'src/components/customer/grid/EmptyCustomers'
import { GridTable } from 'src/components/shared/grid/GridTable'
import {
  CUSTOMERS_GRID_COLUMNS_STORAGE_KEY,
  customerPageSizeOptions,
} from 'src/constants/customer/constants'
import { customerColumns } from 'src/constants/customer/customerGrid'
import { CustomersTableFields, CustomerStatus } from 'src/constants/customer/enums'
import { ADMIN_ROUTES } from 'src/constants/routes'
import type { Customer } from 'src/interfaces/customer'
import { getCustomers, getCustomersLoading } from 'src/selectors/customer'
import { filterCustomers } from 'src/utils/customerList'
import { useGridColumns } from 'src/utils/hooks/grid/useGridColumns'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'

interface Props {
  /** The selected status tab — the table only ever shows one lifecycle state at a time. */
  status: CustomerStatus
  searchQuery: string
}

const getRowId = (row: Customer) => {
  return row.id
}

// Belt-and-braces: `displayName` also has `hideable: false` on its column definition, which
// is what actually stops the hide checkbox from being offered in MUI's default manage-columns
// panel (opened via the column menu's "Manage columns" item). This guard additionally stops a
// programmatic visibility-model change from persisting a hide of that column.
// Scoped to the rows, not the grid: `GridTable` merges what it is given into its outer wrapper,
// so a bare `cursor-pointer` would also cover the empty space below the last row and the
// pagination footer — neither of which opens a customer.
const rowPointerStyles = { '& .MuiDataGrid-row': tw`cursor-pointer` }

const UNHIDEABLE_COLUMNS: Array<string> = [CustomersTableFields.displayName]

const CustomersTableComponent = ({ status, searchQuery }: Props) => {
  const navigate = useNavigate()

  const customers = useSelector(getCustomers)
  const isLoading = useSelector(getCustomersLoading)

  const { paginationModel, setPaginationModel } = useGridPagination(customerPageSizeOptions)
  const { includedColumns, handleAddExcludedColumn, handleRemoveExcludedColumn } = useGridColumns(
    customerColumns,
    CUSTOMERS_GRID_COLUMNS_STORAGE_KEY,
  )

  // The client holds every customer (see CustomersPage's fetch effect), so status and search
  // are both applied here rather than sent to the backend.
  const visibleCustomers = useMemo(() => {
    return filterCustomers(customers, { status, search: searchQuery })
  }, [customers, status, searchQuery])

  // `GridTable` renders `EmptyElement` as a DataGrid overlay slot, with no props of its own, so
  // the search-aware copy has to be bound here. Memoised on the one input it reads so the slot
  // identity stays stable between renders and the grid does not remount the overlay.
  const isSearchUsed = searchQuery.trim() !== ''
  const EmptyElement = useMemo(() => {
    return () => {
      return <EmptyCustomers isSearchUsed={isSearchUsed} />
    }
  }, [isSearchUsed])

  const columnVisibilityModel = useMemo(() => {
    return customerColumns.reduce<GridColumnVisibilityModel>((accumulator, column) => {
      accumulator[column.field] = includedColumns.includes(column.field)

      return accumulator
    }, {})
  }, [includedColumns])

  // `useGridColumns` stores excluded fields as a flat list; the grid speaks a visibility
  // model. Translate both ways so menu changes persist to localStorage across reloads.
  const handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      customerColumns.forEach((column) => {
        if (UNHIDEABLE_COLUMNS.includes(column.field)) {
          return
        }

        if (model[column.field] === false) {
          handleAddExcludedColumn(column.field)

          return
        }

        handleRemoveExcludedColumn(column.field)
      })
    },
    [handleAddExcludedColumn, handleRemoveExcludedColumn],
  )

  // Pinning is intentionally left uncontrolled. `useGridColumns`'s persisted `pinnedColumns`
  // is a flat `Array<string>` with no left/right distinction, so a controlled
  // `pinnedColumns`/`onPinnedColumnsChange` pair built as `{ left: pinnedColumns }` can only
  // ever write back a left pin — clicking "Pin to right" in the shared column menu would
  // silently fail to persist (and the column would snap back to unpinned). Leaving these
  // props off `GridTable` lets `DataGridPro` manage both pin directions natively for the
  // session; only persistence across reloads is lost.
  const handleRowClick = useCallback(
    (params: GridRowParams<Customer>) => {
      navigate(ADMIN_ROUTES.ADMIN_DASHBOARD_CUSTOMER_URL.replace(':customerId', String(params.id)))
    },
    [navigate],
  )

  return (
    <GridTable<Customer>
      rows={visibleCustomers}
      columns={customerColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={customerPageSizeOptions}
      initialState={{
        sorting: { sortModel: [{ field: CustomersTableFields.createdAt, sort: 'desc' }] },
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      EmptyElement={EmptyElement}
      tableDataTestId="customers-list-table"
      rowDataTestIdPrefix="customers-list"
      onRowClick={handleRowClick}
      className={rowPointerStyles}
      fillHeight
    />
  )
}

export const CustomersTable = memo(CustomersTableComponent)
